import React from 'react'
import {
  KestrelInspectionType,
  KVAnnotation,
  KVNavyAnnotation,
  KVRooftopAnnotation,
} from 'types'

import { NavyDamageSelector } from './NavyDamageSelector'
import { getAllNavyDamageTypes } from './navyDamageTypes'
import { RooftopDamageSelector } from './RooftopDamageSelector'
import { getAllRooftopDamageTypes } from './rooftopDamageTypes'
import { DamageTypeHandler } from './types'

export const navyDamageHandler: DamageTypeHandler = {
  inspectionType: KestrelInspectionType.NAVY,

  getInitialSelection: (annotation: KVAnnotation): string[] => {
    if (!annotation.navyAnnotation) {
      return []
    }

    const selected: string[] = []
    getAllNavyDamageTypes().forEach((damageType) => {
      if (
        annotation.navyAnnotation?.[
          damageType.navyProperty as keyof KVNavyAnnotation
        ]
      ) {
        selected.push(damageType.id)
      }
    })

    return selected
  },

  updateAnnotation: (
    annotation: KVAnnotation,
    selectedTypes: string[],
    setAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): void => {
    const newNavyAnnotation = new KVNavyAnnotation()

    getAllNavyDamageTypes().forEach((damageType) => {
      newNavyAnnotation[damageType.navyProperty as keyof KVNavyAnnotation] =
        selectedTypes.includes(damageType.id)
    })

    setAnnotation((prev) => ({
      ...prev,
      navyAnnotation: newNavyAnnotation,
    }))
  },

  renderSelector: (
    selectedTypes: string[],
    onSelectionChange: (types: string[]) => void,
    annotation: KVAnnotation
  ): React.ReactNode => {
    return (
      <NavyDamageSelector
        selectedTypes={selectedTypes}
        onSelectionChange={onSelectionChange}
        annotation={annotation}
      />
    )
  },
}

export const rooftopDamageHandler: DamageTypeHandler = {
  inspectionType: KestrelInspectionType.ROOFTOP,

  getInitialSelection: (annotation: KVAnnotation): string[] => {
    if (!annotation.rooftopAnnotation) {
      return []
    }

    const selected: string[] = []
    const rooftopTypes = getAllRooftopDamageTypes()

    rooftopTypes.forEach((damageType) => {
      if (
        annotation.rooftopAnnotation?.[
          damageType.rooftopProperty as keyof KVRooftopAnnotation
        ]
      ) {
        selected.push(damageType.id)
      }
    })

    return selected
  },

  updateAnnotation: (
    annotation: KVAnnotation,
    selectedTypes: string[],
    setAnnotation: React.Dispatch<React.SetStateAction<KVAnnotation>>
  ): void => {
    const newRooftopAnnotation = new KVRooftopAnnotation()

    getAllRooftopDamageTypes().forEach((damageType) => {
      newRooftopAnnotation[
        damageType.rooftopProperty as keyof KVRooftopAnnotation
      ] = selectedTypes.includes(damageType.id)
    })

    setAnnotation((prev) => ({
      ...prev,
      rooftopAnnotation: newRooftopAnnotation,
    }))
  },

  renderSelector: (
    selectedTypes: string[],
    onSelectionChange: (types: string[]) => void,
    annotation: KVAnnotation
  ): React.ReactNode => {
    return (
      <RooftopDamageSelector
        selectedTypes={selectedTypes}
        onSelectionChange={onSelectionChange}
        annotation={annotation}
      />
    )
  },
}

// Registry of all damage type handlers
export const damageTypeHandlers = new Map<
  KestrelInspectionType,
  DamageTypeHandler
>([
  [KestrelInspectionType.NAVY, navyDamageHandler],
  [KestrelInspectionType.ROOFTOP, rooftopDamageHandler],
])

export const getDamageTypeHandler = (
  inspectionType: KestrelInspectionType
): DamageTypeHandler => {
  const handler = damageTypeHandlers.get(inspectionType)
  if (!handler) {
    return navyDamageHandler
  }
  return handler
}
